<template>
  <div class="pressure-dial">
    <div v-if="title" class="dial-title">{{ title }}</div>
    <div v-if="subtitle" class="dial-subtitle">{{ subtitle }}</div>
    <svg class="dial-svg" viewBox="0 0 360 360" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <!-- 表盘外圈 -->
      <circle :cx="cx" :cy="cy" :r="rOuter" fill="#ffffff" stroke="#bfbfbf" stroke-width="1" />

      <!-- 同心压力刻度环 -->
      <circle
        v-for="ring in rings"
        :key="'ring' + ring.value"
        :cx="cx"
        :cy="cy"
        :r="ring.r"
        fill="none"
        stroke="#e6e6e6"
        stroke-width="1"
      />

      <!-- 24 小时放射线 -->
      <line
        v-for="s in spokes"
        :key="'spoke' + s.hour"
        :x1="cx"
        :y1="cy"
        :x2="s.x"
        :y2="s.y"
        stroke="#ededed"
        stroke-width="0.8"
      />

      <!-- 压力上下限 -->
      <circle
        v-if="highRadius != null"
        :cx="cx"
        :cy="cy"
        :r="highRadius"
        fill="none"
        stroke="#e02020"
        stroke-width="1.6"
        stroke-dasharray="6 4"
      />
      <circle
        v-if="lowRadius != null"
        :cx="cx"
        :cy="cy"
        :r="lowRadius"
        fill="none"
        stroke="#e02020"
        stroke-width="1.6"
        stroke-dasharray="6 4"
      />

      <!-- 24 小时压力曲线 -->
      <polyline
        v-for="(points, i) in seriesPaths"
        :key="'series' + i"
        :points="points"
        fill="none"
        stroke="#1f7bb6"
        stroke-width="2"
        stroke-linejoin="round"
        stroke-linecap="round"
      />

      <!-- 刻度值 -->
      <text
        v-for="ring in rings"
        :key="'label' + ring.value"
        :x="cx"
        :y="cy - ring.r - 5"
        text-anchor="middle"
        class="axis-label"
      >{{ ring.label }}</text>
      <text :x="cx" :y="cy" text-anchor="middle" dominant-baseline="middle" class="axis-label">0</text>

      <!-- 小时标签 -->
      <text
        v-for="h in hours"
        :key="'hour' + h.value"
        :x="h.x"
        :y="h.y"
        text-anchor="middle"
        dominant-baseline="middle"
        class="hour-label"
      >{{ h.label }}</text>
    </svg>

    <div v-if="!points.length" class="dial-empty">暂无24小时压力数据</div>

    <div class="dial-legend">
      <span class="legend-item"><i class="legend-line"></i>{{ seriesLabel }}</span>
      <span v-if="highLimit != null" class="legend-item">
        <i class="legend-block"></i>压力上限：{{ formatLimit(highLimit) }}{{ unit }}
      </span>
      <span v-if="lowLimit != null" class="legend-item">
        <i class="legend-block"></i>压力下限：{{ formatLimit(lowLimit) }}{{ unit }}
      </span>
    </div>
  </div>
</template>

<script>
const CX = 180
const CY = 180
const R_OUTER = 136
const R_LABEL = 158

function polar(cx, cy, r, degFromTopCw) {
  const rad = ((degFromTopCw - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function parseTime(value) {
  if (value == null) return NaN
  if (typeof value === 'number') return value
  const text = String(value).trim()
  if (!text) return NaN
  if (/^\d{13}$/.test(text)) return Number(text)
  return Date.parse(text.replace(' ', 'T'))
}

/** 为轴上限挑一个整齐的刻度步长（1 / 2 / 2.5 / 5 / 10 × 10^n） */
function niceStep(rawStep) {
  if (!(rawStep > 0)) return 1
  const exp = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const candidates = [1, 2, 2.5, 5, 10]
  for (let i = 0; i < candidates.length; i++) {
    const step = candidates[i] * exp
    if (step >= rawStep) return step
  }
  return 10 * exp
}

export default {
  name: 'PressureDialGauge',
  props: {
    value: { type: [Number, String], default: null },
    trend: { type: Array, default: () => [] },
    min: { type: Number, default: 0 },
    unit: { type: String, default: 'kPa' },
    lowLimit: { type: Number, default: null },
    highLimit: { type: Number, default: null },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    seriesLabel: { type: String, default: '压力' }
  },
  data() {
    return { cx: CX, cy: CY, rOuter: R_OUTER }
  },
  computed: {
    numericValue() {
      const n = Number(this.value)
      return Number.isFinite(n) ? n : null
    },
    points() {
      const now = Date.now()
      const list = (this.trend || [])
        .map(p => ({ t: parseTime(p && (p.time || p.sampleTime)), v: Number(p && p.value) }))
        .filter(p => Number.isFinite(p.t) && Number.isFinite(p.v))
        .sort((a, b) => a.t - b.t)
      if (!list.length) return []
      const last = list[list.length - 1].t
      // 设备离线时以最后一条数据为窗口终点，避免图表整片空白
      const end = now - last <= 2 * 3600 * 1000 ? now : last
      const start = end - 24 * 3600 * 1000
      return list
        .filter(p => p.t >= start && p.t <= end)
        .map(p => {
          const d = new Date(p.t)
          const hours = d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600
          return { value: p.v, angle: (hours / 24) * 360 }
        })
    },
    axisMax() {
      const values = this.points.map(p => p.value)
      if (this.numericValue != null) values.push(this.numericValue)
      if (this.highLimit != null) values.push(this.highLimit)
      if (this.lowLimit != null) values.push(this.lowLimit)
      const rawMax = values.length ? Math.max.apply(null, values) : 1
      if (!(rawMax > this.min)) return this.min + 1
      const step = niceStep((rawMax - this.min) / 4)
      return Math.ceil((rawMax - this.min) / step) * step + this.min
    },
    axisStep() {
      return niceStep((this.axisMax - this.min) / 4)
    },
    rings() {
      const out = []
      const count = Math.round((this.axisMax - this.min) / this.axisStep)
      for (let i = 1; i <= count; i++) {
        const value = this.min + this.axisStep * i
        out.push({ value, label: this.formatAxis(value), r: this.radiusFor(value) })
      }
      return out
    },
    spokes() {
      return Array.from({ length: 24 }, (_, hour) => {
        const p = polar(CX, CY, R_OUTER, hour * 15)
        return { hour, x: p.x, y: p.y }
      })
    },
    hours() {
      return Array.from({ length: 24 }, (_, hour) => {
        const p = polar(CX, CY, R_LABEL, hour * 15)
        return { value: hour, label: String(hour).padStart(2, '0'), x: p.x, y: p.y }
      })
    },
    seriesPaths() {
      const paths = []
      let current = []
      let prevAngle = null
      this.points.forEach(p => {
        // 跨过 00:00 时断开，避免曲线横穿圆心
        if (prevAngle != null && p.angle - prevAngle < -180 && current.length) {
          paths.push(current.join(' '))
          current = []
        }
        const pt = polar(CX, CY, this.radiusFor(p.value), p.angle)
        current.push(`${pt.x.toFixed(1)},${pt.y.toFixed(1)}`)
        prevAngle = p.angle
      })
      if (current.length) paths.push(current.join(' '))
      return paths
    },
    lowRadius() {
      return this.limitRadius(this.lowLimit)
    },
    highRadius() {
      return this.limitRadius(this.highLimit)
    }
  },
  methods: {
    radiusFor(value) {
      const span = this.axisMax - this.min || 1
      const ratio = Math.max(0, Math.min(1, (Number(value) - this.min) / span))
      return ratio * R_OUTER
    },
    limitRadius(value) {
      const n = Number(value)
      if (!Number.isFinite(n) || n < this.min || n > this.axisMax) return null
      return this.radiusFor(n)
    },
    formatAxis(value) {
      if (this.axisMax <= 10) return String(Math.round(value * 100) / 100)
      return String(Math.round(value))
    },
    formatLimit(value) {
      const n = Number(value)
      return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : '—'
    }
  }
}
</script>

<style scoped>
.pressure-dial {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}
.dial-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  text-align: center;
  line-height: 1.4;
}
.dial-subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: #86909c;
  text-align: center;
}
.dial-svg {
  width: 100%;
  max-width: 380px;
  height: auto;
  display: block;
  margin-top: 4px;
}
.axis-label {
  font-size: 10px;
  fill: #8c8c8c;
  font-family: 'Segoe UI', system-ui, sans-serif;
}
.hour-label {
  font-size: 11px;
  fill: #595959;
  font-family: 'Segoe UI', system-ui, sans-serif;
}
.dial-empty {
  margin-top: -8px;
  font-size: 12px;
  color: #86909c;
}
.dial-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
  padding: 6px 10px;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  font-size: 12px;
  color: #4e5969;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.legend-line {
  width: 18px;
  height: 0;
  border-top: 2px solid #1f7bb6;
}
.legend-block {
  width: 10px;
  height: 10px;
  background: #e02020;
  border-radius: 1px;
}
</style>
